import React, { useState, useEffect, useRef } from 'react';
import { Key, Upload, Plus, Trash2, RefreshCw, Save } from 'lucide-react';
import Anthropic from '@anthropic-ai/sdk';
import { getGlobalData, saveGlobalData, getProjectData, saveProjectData } from '../utils/projectStorage';
import './Settings.css';

interface ContentUrls {
  company: string[];
  audience: string[];
  problems: string[];
  productLine: string[];
  competition: string[];
}

const Settings: React.FC = () => {
  const [apiKey, setApiKey] = useState('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');
  const [apiKeyStatus, setApiKeyStatus] = useState<'untested' | 'testing' | 'valid' | 'invalid'>('untested');

  const [ingestionPrompt, setIngestionPrompt] = useState(`Using the ingested company knowledge and motivational dimensions framework, provide sales guidance that:
- Addresses specific customer questions and objections
- Tailors responses to the selected motivational dimension
- References relevant product features and benefits
- Provides actionable sales strategies`);

  const [alignmentGuide, setAlignmentGuide] = useState(`🏆 ACHIEVER - Frame everything around excellence and distinction

Problem framing: Position as falling short of potential, missing competitive edge
Solution approach: Show how MCode creates standout performance and recognition
Outcome focus: Being the best, exceeding industry standards, distinctive results
Next step logic: Choose products that demonstrate superiority and measurable wins
Communication style: Confident, results-focused, emphasizing uniqueness and achievement

🎯 DRIVER - Structure as obstacle → breakthrough → victory

Problem framing: Identify specific barriers preventing progress toward goals
Solution approach: Present MCode as the tool to break through and drive forward
Outcome focus: Clear goal achievement, momentum, overcoming challenges
Next step logic: Choose products that deliver immediate, measurable progress
Communication style: Direct, action-oriented, emphasizing forward movement and results

💡 INFLUENCER - Center on human potential and transformation

Problem framing: Unrealized potential in people, missed transformation opportunities
Solution approach: Show how MCode unlocks and inspires human greatness
Outcome focus: Transforming lives, inspiring others, creating ripple effects
Next step logic: Choose products that create inspiring transformation experiences
Communication style: Visionary, inspiring, emphasizing human impact and possibility

📚 LEARNER - Build around knowledge, understanding, and expertise

Problem framing: Lack of deep understanding, superficial insights, knowledge gaps
Solution approach: Present MCode's scientific depth and learning opportunities
Outcome focus: Mastery, deeper understanding, expertise development
Next step logic: Choose products that provide learning and knowledge expansion
Communication style: Educational, detailed, emphasizing research and methodology

⚙️ OPTIMIZER - Focus on improvement and systematic enhancement

Problem framing: Inefficiencies, broken processes, suboptimal performance
Solution approach: Show how MCode systematically improves existing systems
Outcome focus: Better efficiency, enhanced performance, refined processes
Next step logic: Choose products that optimize current operations
Communication style: Practical, systematic, emphasizing improvement and refinement

🎼 ORCHESTRATOR - Present as strategic framework for organizational leadership

Problem framing: Lack of coordination, unclear structure, leadership challenges
Solution approach: Position MCode as comprehensive organizational framework
Outcome focus: Strategic alignment, coordinated teams, effective leadership
Next step logic: Choose products that provide systematic team organization
Communication style: Strategic, comprehensive, emphasizing leadership and coordination

🤝 RELATOR - Frame around relationships and collective success

Problem framing: Disconnected teams, relationship challenges, lack of collaboration
Solution approach: Show how MCode strengthens connections and team unity
Outcome focus: Stronger relationships, team harmony, collective achievement
Next step logic: Choose products that bring teams together and build connections
Communication style: Collaborative, team-focused, emphasizing shared success

🔮 VISIONARY - Present as creative breakthrough for future possibilities

Problem framing: Stuck in current reality, limited thinking, innovation barriers
Solution approach: Position MCode as catalyst for innovative transformation
Outcome focus: Creative breakthroughs, future possibilities, innovative solutions
Next step logic: Choose products that enable creative exploration and innovation
Communication style: Imaginative, future-focused, emphasizing creativity and possibility
`);
  
  const [salesPrompt, setSalesPrompt] = useState(`You are a sales enablement expert for Motivation Code (MCode), a motivation-based assessment company. Your role is to help craft perfect responses to sales questions and objections by completely aligning with the prospect's motivational wiring.
Your Process:
Identify the core question/objection - What's really being asked? Look for the question behind the question.
Determine the prospect's primary motivational dimension - This should be provided or you should ask for it.
Craft a COMPLETELY dimension-aligned response - Every aspect should resonate with how that dimension thinks, decides, and acts.
Include a specific next step with the most motivationally-aligned MCode product.

Explain your reasoning - Why this total approach works for this dimension, product choice logic, plus alternatives.`);

  const [processing, setProcessing] = useState(false);
  const [processStatus, setProcessStatus] = useState('');
  const [spacesStatus, setSpacesStatus] = useState<'untested' | 'testing' | 'valid' | 'invalid'>('untested');
  const [uploadedFiles, setUploadedFiles] = useState<{[key: string]: File[]}>({
    company: [],
    audience: [],
    problems: [],
    productLine: [],
    competition: [],
    files: []
  });
  const [motivationalDimensionsFile, setMotivationalDimensionsFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Load data from global data and project data
    const globalData = getGlobalData();
    const projectData = getProjectData('sales-assistant');

    // Load API key from global data
    if (globalData.apiKey) {
      setApiKey(globalData.apiKey);
      setApiKeyStatus('valid');
    }

    if (projectData.ingestionPrompt) {
      setIngestionPrompt(projectData.ingestionPrompt);
    }
    if (projectData.salesPrompt) {
      setSalesPrompt(projectData.salesPrompt);
    }

    // Note: We can't restore the File object, but we can show that a file was uploaded
    if (globalData.motivationalDimensionsFile) {
      // Create a placeholder to show the file was uploaded
      // The actual content is already in global data
    }
  }, []);

  const saveApiKey = () => {
    const globalData = getGlobalData();
    globalData.apiKey = apiKey;
    saveGlobalData(globalData);
    // Update API status in Layout
    window.dispatchEvent(new Event('storage'));
  };

  const testApiKey = async () => {
    setApiKeyStatus('testing');
    try {
      const anthropic = new Anthropic({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true
      });

      // Test the API key with a simple request
      await anthropic.messages.create({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 10,
        messages: [{
          role: 'user',
          content: 'Test'
        }]
      });

      setApiKeyStatus('valid');
      saveApiKey();
    } catch (error) {
      setApiKeyStatus('invalid');
      console.error('API key test failed:', error);
    }
  };

  const testSpacesConnection = async () => {
    setSpacesStatus('testing');
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/files/test-connection`);
      
      if (response.ok) {
        setSpacesStatus('valid');
      } else {
        setSpacesStatus('invalid');
      }
    } catch (error) {
      setSpacesStatus('invalid');
      console.error('Spaces connection test failed:', error);
    }
  };


  const handleFileUpload = (category: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFiles(prev => ({
        ...prev,
        [category]: [...(prev[category as keyof typeof prev] || []), file]
      }));
      event.target.value = ''; // Reset input
    }
  };

  const handleMotivationalDimensionsUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setMotivationalDimensionsFile(file);
      
      // Read and save the file contents to global data
      try {
        const fileContent = await file.text();
        const globalData = getGlobalData();
        globalData.motivationalDimensionsFile = {
          name: file.name,
          content: fileContent,
          lastModified: file.lastModified,
          size: file.size
        };
        saveGlobalData(globalData);
      } catch (error) {
        console.error('Error reading motivational dimensions file:', error);
      }
    }
  };

  const removeFile = () => {
    // Remove from global data
    const globalData = getGlobalData();
    globalData.motivationalDimensionsFile = undefined;
    saveGlobalData(globalData);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };


  const processContent = async () => {
    setProcessing(true);
    setProcessStatus('Processing content...');

    try {
      const anthropic = new Anthropic({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true
      });

      // Save prompts to project data
      const projectData = getProjectData('sales-assistant');
      projectData.ingestionPrompt = ingestionPrompt;
      projectData.salesPrompt = salesPrompt;

      // Prepare URLs for Claude to fetch
      setProcessStatus('Preparing URLs...');
      const urlsList: { [key: string]: string[] } = {};

      // Upload files to Anthropic via backend
      setProcessStatus('Uploading files to Anthropic...');
      const fileIds: string[] = [];
      
      if (uploadedFiles.files && uploadedFiles.files.length > 0) {
        try {
          const formData = new FormData();
          
          uploadedFiles.files.forEach(file => {
            formData.append('files', file);
          });
          
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
          const uploadResponse = await fetch(`${apiUrl}/api/files/upload-to-anthropic`, {
            method: 'POST',
            body: formData
          });
          
          if (!uploadResponse.ok) {
            throw new Error(`Upload failed: ${uploadResponse.statusText}`);
          }
          
          const uploadResult = await uploadResponse.json();
          
          for (const file of uploadResult.files) {
            if (file.anthropicFileId) {
              fileIds.push(file.anthropicFileId);
              setProcessStatus(`Uploaded ${file.name} successfully (ID: ${file.anthropicFileId})`);
            } else {
              setProcessStatus(`Error uploading ${file.name}: ${file.error}`);
            }
          }
          
        } catch (error) {
          console.error('Error uploading files:', error);
          setProcessStatus(`Error uploading files: ${error}`);
        }
      }
      
      // Store file IDs in project data for use in chat
      projectData.fileIds = fileIds;

      // Get motivational dimensions content from global data
      const globalData = getGlobalData();
      let motivationalDimensionsContent = globalData.motivationalDimensionsFile?.content || '';
      
      // If there's a new file uploaded that hasn't been saved yet, use that instead
      if (motivationalDimensionsFile) {
        motivationalDimensionsContent = await motivationalDimensionsFile.text();
        // Save it to global data
        globalData.motivationalDimensionsFile = {
          name: motivationalDimensionsFile.name,
          content: motivationalDimensionsContent,
          lastModified: motivationalDimensionsFile.lastModified,
          size: motivationalDimensionsFile.size
        };
        saveGlobalData(globalData);
      }

      // Create a combined content prompt that includes URLs for Claude to fetch
      setProcessStatus('Creating Claude session...');
      const combinedContent = `
${ingestionPrompt}

Please analyze the following content from various sources:

## Uploaded Files
${fileIds.length > 0 ? `${fileIds.length} PDF file(s) have been uploaded and will be attached to sales enablement conversations.` : 'No files uploaded yet.'}

## Motivational Dimensions Framework
${motivationalDimensionsContent ? `
The following is the complete MCode Motivational Dimensions framework that you must use when providing sales guidance:

${motivationalDimensionsContent}

## Dimensions Alignment Guide
${alignmentGuide}

Please ensure you fully understand and apply these motivational dimensions when tailoring your responses to specific personality types.
` : 'No motivational dimensions file uploaded yet.'}

Note: Please analyse que attached files to extract relevant information about the company, audience, problems, product line, and competition. Use the motivational dimensions framework to tailor your responses to specific customer personality types.
Do not include any dimensions or motivations that are not in the lists above. Please be very precise with the spelling and capitalization of dimension and motivation names.`;

      // Store the combined content for use in chat sessions
      projectData.ingestedContent = combinedContent;

      // Send initial message to Claude to establish context
      setProcessStatus('Initializing Claude session...');
      
      try {
        const response = await anthropic.messages.create({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 16384,
          system: [
            {
              type: 'text',
              text: combinedContent,
              cache_control: { type: 'ephemeral' }
            }
          ],
          messages: [{
            role: 'user',
            content: [
              {
                type: 'text',
                text: 'I have provided you with company information, target audience details, problems we solve, product line information, competition analysis, and the MCode motivational dimensions framework. Please confirm that you have processed and understood all this information, and that you are ready to provide sales guidance based on this knowledge. Use the web search tool to fetch any additional information you may need.',
                cache_control: { type: 'ephemeral' }
              }
            ]
          }]
        });

        projectData.ingestionResponse = response;

        console.log('Claude session initialized:', response);
        // Initialize message history
        const initialHistory = [
          {
            id: Date.now().toString(),
            role: 'user' as const,
            content: 'System: Content ingested and processed.',
            timestamp: new Date()
          },
          {
            id: (Date.now() + 1).toString(),
            role: 'assistant' as const,
            content: response.content[0].type === 'text' ? response.content[0].text : '',
            timestamp: new Date()
          }
        ];
        
        projectData.messageHistory = initialHistory;

        // Save session info
        const sessionInfo = {
          sessionId: `session-${Date.now()}`,
          createdAt: new Date().toISOString(),
          status: 'active',
          contentIngested: true,
          lastMessageId: initialHistory[initialHistory.length - 1].id
        };
        projectData.claudeSession = sessionInfo;

        setProcessStatus('Content processed and Claude session initialized successfully!');
      } catch (error) {
        console.error('Error initializing Claude session:', error);
        setProcessStatus('Content saved but failed to initialize Claude session. You can still use the chat.');
        
        // Save session info even if Claude call fails
        const sessionInfo = {
          sessionId: `session-${Date.now()}`,
          createdAt: new Date().toISOString(),
          status: 'active',
          contentIngested: true
        };
        projectData.claudeSession = sessionInfo;
      }
      
      // Save all project data
      saveProjectData('sales-assistant', projectData);
    } catch (error) {
      setProcessStatus('Error processing content');
      console.error('Process error:', error);
    } finally {
      setProcessing(false);
    }
  };

  const categories = [
    { key: 'company', label: 'Company', description: 'Company information, about us, mission' },
    { key: 'audience', label: 'Audience', description: 'Target audience, personas, demographics' },
    { key: 'problems', label: 'Problems We Solve', description: 'Pain points, challenges, use cases' },
    { key: 'productLine', label: 'Product Line', description: 'Features, specifications, benefits' },
    { key: 'competition', label: 'Competition', description: 'Competitive analysis, differentiators' }
  ];

  return (
    <div className="settings-page">
      <h2>Settings</h2>

      {/* API Configuration */}
      <section className="settings-section">
        <h3><Key size={20} /> API Configuration</h3>
        <div className="api-config">
          <input
            type="password"
            placeholder="Enter your Anthropic API Key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className="api-key-input"
          />
          <button 
            onClick={testApiKey} 
            className="test-btn"
            disabled={!apiKey || apiKeyStatus === 'testing'}
          >
            {apiKeyStatus === 'testing' ? 'Testing...' : 'Test Connection'}
          </button>
          <div className={`api-status ${apiKeyStatus}`}>
            {apiKeyStatus === 'untested' && 'Not tested'}
            {apiKeyStatus === 'testing' && 'Testing...'}
            {apiKeyStatus === 'valid' && '✓ Connected'}
            {apiKeyStatus === 'invalid' && '✗ Invalid key'}
          </div>
        </div>
      </section>

      {/* DigitalOcean Spaces Configuration */}
      {/*<section className="settings-section">*/}
      {/*  <h3>DigitalOcean Spaces</h3>*/}
      {/*  <div className="api-key-group">*/}
      {/*    <div className="spaces-info">*/}
      {/*      <p>File uploads are handled via backend presigned URLs</p>*/}
      {/*      <p>This avoids CORS issues and keeps credentials secure</p>*/}
      {/*    </div>*/}
      {/*    <button */}
      {/*      onClick={testSpacesConnection} */}
      {/*      className="test-btn"*/}
      {/*      disabled={spacesStatus === 'testing'}*/}
      {/*    >*/}
      {/*      {spacesStatus === 'testing' ? 'Testing...' : 'Test Connection'}*/}
      {/*    </button>*/}
      {/*    <div className={`api-status ${spacesStatus}`}>*/}
      {/*      {spacesStatus === 'untested' && 'Not tested'}*/}
      {/*      {spacesStatus === 'testing' && 'Testing...'}*/}
      {/*      {spacesStatus === 'valid' && '✓ Connected'}*/}
      {/*      {spacesStatus === 'invalid' && '✗ Connection failed'}*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*</section>*/}

      {/* Files */}
      <section className="settings-section">
        <h3>Files</h3>
        
        <input
          type="file"
          multiple
          accept=".pdf"
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            setUploadedFiles(prev => ({
              ...prev,
              files: [...prev.files, ...files]
            }));
          }}
          className="file-input"
        />
        
        <ul className="url-list">
          {uploadedFiles.files?.map((file, index) => (
            <li key={`file-${index}`}>
              <span>{file.name}</span>
              <button 
                onClick={() => {
                  setUploadedFiles(prev => ({
                    ...prev,
                    files: prev.files.filter((_, i) => i !== index)
                  }));
                }} 
                className="delete-btn"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      </section>

      {/* Motivational Dimensions */}
      <section className="settings-section">
        <h3>MCode Motivational Dimensions</h3>
        
        {(() => {
          const globalData = getGlobalData();
          return globalData.motivationalDimensionsFile ? (
            <div className="file-display">
              <div className="file-info">
                <Upload size={20} />
                <div className="file-details">
                  <span className="file-name">{globalData.motivationalDimensionsFile.name}</span>
                  <span className="file-meta">
                    {formatFileSize(globalData.motivationalDimensionsFile.size)} • 
                    {new Date(globalData.motivationalDimensionsFile.lastModified).toLocaleDateString()}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={removeFile}
                  className="remove-file-btn"
                  title="Remove file"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="file-content-preview">
                <strong>File Content Preview:</strong>
                <div className="content-preview">
                  {globalData.motivationalDimensionsFile.content.substring(0, 200)}
                  {globalData.motivationalDimensionsFile.content.length > 200 && '...'}
                </div>
              </div>
            </div>
          ) : (
            <div className="file-upload">
              <input
                ref={fileInputRef}
                type="file"
                id="mcodeDimensionsFile"
                accept=".txt,.md,.json"
                onChange={handleMotivationalDimensionsUpload}
                className="file-input"
              />
              <label htmlFor="mcodeDimensionsFile" className="file-upload-label">
                <Upload size={20} />
                <span>Upload MCode Dimensions File</span>
                <span className="file-types">Supported: .txt, .md, .json (max 5MB)</span>
              </label>
            </div>
          );
        })()}
        
        <p className="field-description">
          Upload a file containing MCODE dimensional definitions. This file will be shared across all projects.
        </p>
      </section>

      {/* Dimension Alignment Guide */}
      <section className="settings-section">
        <h3>Dimensions Alignment Guide</h3>
        <div className="prompt-section">
          <textarea
              value={alignmentGuide}
              onChange={(e) => setAlignmentGuide(e.target.value)}
              className="prompt-textarea"
              rows={6}
          />
        </div>
      </section>

      {/* Claude Prompts */}
      <section className="settings-section">
        <h3>Claude Prompts</h3>
        <div className="prompt-section">
          <h4>Content Ingestion Prompt</h4>
          <textarea
            value={ingestionPrompt}
            onChange={(e) => setIngestionPrompt(e.target.value)}
            className="prompt-textarea"
            rows={6}
          />
        </div>
        <div className="prompt-section">
          <h4>Sales Enablement Prompt</h4>
          <textarea
            value={salesPrompt}
            onChange={(e) => setSalesPrompt(e.target.value)}
            className="prompt-textarea"
            rows={6}
          />
        </div>
      </section>

      {/* Process Content */}
      <section className="settings-section">
        <h3><RefreshCw size={20} /> Content Processing</h3>
        <button 
          onClick={processContent}
          className="process-btn"
          disabled={processing || apiKeyStatus !== 'valid'}
        >
          {processing ? 'Processing...' : 'Process Content'}
        </button>
        {processStatus && (
          <div className="process-status">
            {processStatus}
          </div>
        )}
      </section>
    </div>
  );
};

export default Settings;